import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Box,
  Title,
  Text,
  Paper,
  Stack,
  Group,
  Badge,
  Button,
  useMantineColorScheme,
  Progress,
  Divider,
  Center,
  RingProgress,
  Accordion,
  ActionIcon,
  Tooltip,
  Loader,
} from '@mantine/core';
import { IconArrowLeft, IconCheck, IconX } from '@tabler/icons-react';
import { ResultadoExamen, DetallePreguntaResultado } from '../types/examen.types';
import examenService from '../services/examenService';

const ResultadosPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { intentoId } = useParams<{ intentoId: string }>();
  const { colorScheme } = useMantineColorScheme();
  
  const [resultado, setResultado] = useState<ResultadoExamen | null>(location.state?.resultado || null);
  const [loading, setLoading] = useState<boolean>(!location.state?.resultado);
  const [error, setError] = useState<string>('');
  const [filtro, setFiltro] = useState<'todas' | 'correctas' | 'incorrectas' | 'enblanco'>('todas');

  // Cargar resultados si no vienen desde state
  useEffect(() => {
    if (!resultado && intentoId) {
      cargarResultados();
    }
  }, [intentoId]);

  const cargarResultados = async () => {
    try {
      setLoading(true);
      const data = await examenService.obtenerResultados(Number(intentoId));
      setResultado(data);
    } catch (err: any) {
      console.error('Error cargando resultados:', err);
      setError('No se pudieron cargar los resultados');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Center style={{ minHeight: '100vh' }}>
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">Cargando resultados...</Text>
        </Stack>
      </Center>
    );
  }

  if (error || !resultado) {
    return (
      <Center style={{ minHeight: '100vh' }}>
        <Stack align="center">
          <Text size="xl">{error || 'No se encontraron resultados'}</Text>
          <Button onClick={() => navigate('/estudiante/simulador')}>Volver al simulador</Button>
        </Stack>
      </Center>
    );
  }

  const formatearTiempo = (segundos: number): string => {
    const horas = Math.floor(segundos / 3600);
    const mins = Math.floor((segundos % 3600) / 60);
    const secs = segundos % 60;

    if (horas > 0) {
      return `${horas}h ${mins}m ${secs}s`;
    }
    return `${mins}m ${secs}s`;
  };

  const obtenerPreguntasFiltradas = (): DetallePreguntaResultado[] => {
    switch (filtro) {
      case 'correctas':
        return resultado.preguntas.filter(p => p.esCorrecta);
      case 'incorrectas':
        return resultado.preguntas.filter(p => !p.esCorrecta && p.respuestaUsuario !== null);
      case 'enblanco':
        return resultado.preguntas.filter(p => p.respuestaUsuario === null);
      default:
        return resultado.preguntas;
    }
  };

  const preguntasFiltradas = obtenerPreguntasFiltradas();

  return (
    <Box
      style={{
        minHeight: '100vh',
        background:
          colorScheme === 'dark'
            ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
            : 'linear-gradient(135deg, #f7f3ee 0%, #f2ede6 100%)',
        padding: '24px',
      }}
    >
      <Paper
        shadow="md"
        radius="md"
        p="lg"
        withBorder
        style={{
          backgroundColor:
            colorScheme === 'dark' ? '#1e293b' : 'rgba(255,255,255,0.95)',
          maxWidth: '900px',
          margin: '0 auto'
        }}
      >
        {/* Header compacto */}
        <Group justify="space-between" align="center" mb="lg">
          <Box>
            <Title order={3}>Resultados</Title>
            <Text size="xs" c="dimmed">{resultado.tipoExamen}</Text>
          </Box>
          <Tooltip label="Volver al simulador">
            <ActionIcon
              variant="default"
              size="lg"
              radius="md"
              onClick={() => navigate('/estudiante/simulador')}
            >
              <IconArrowLeft size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>

        {/* Estadísticas compactas */}
        <Group justify="center" mb="md" grow>
          <Paper 
            p="sm" 
            withBorder 
            radius="md"
            style={{ 
              textAlign: 'center',
              backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.6)' : '#fafafa',
            }}
          >
            <Text size="lg" fw={700}>{resultado.totalPreguntas}</Text>
            <Text size="xs" c="dimmed">Total</Text>
          </Paper>

          <Paper 
            p="sm" 
            withBorder 
            radius="md"
            style={{ 
              textAlign: 'center',
              backgroundColor: colorScheme === 'dark' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.05)',
              borderColor: 'rgba(34, 197, 94, 0.3)',
            }}
          >
            <Text size="lg" fw={700} style={{ color: colorScheme === 'dark' ? '#4ade80' : '#16a34a' }}>{resultado.correctas}</Text>
            <Text size="xs" c="dimmed">Correctas</Text>
          </Paper>

          <Paper 
            p="sm" 
            withBorder 
            radius="md"
            style={{ 
              textAlign: 'center',
              backgroundColor: colorScheme === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
              borderColor: 'rgba(239, 68, 68, 0.3)',
            }}
          >
            <Text size="lg" fw={700} style={{ color: colorScheme === 'dark' ? '#f87171' : '#dc2626' }}>{resultado.incorrectas}</Text>
            <Text size="xs" c="dimmed">Incorrectas</Text>
          </Paper>

          <Paper 
            p="sm" 
            withBorder 
            radius="md"
            style={{ 
              textAlign: 'center',
              backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.6)' : '#fafafa',
            }}
          >
            <Text size="lg" fw={700}>{resultado.enBlanco}</Text>
            <Text size="xs" c="dimmed">En Blanco</Text>
          </Paper>
        </Group>

        {/* Resumen compacto */}
        <Group justify="center" align="center" mb="md" gap="xl">
          <RingProgress
            size={140}
            thickness={14}
            sections={[
              { value: resultado.porcentajeAciertos, color: colorScheme === 'dark' ? '#4ade80' : '#16a34a' },
            ]}
            rootColor={colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(139, 92, 46, 0.2)'}
            label={
              <Center>
                <div>
                  <Text size="lg" fw={700} ta="center" style={{ color: colorScheme === 'dark' ? '#4ade80' : '#16a34a' }}>
                    {Math.round(resultado.porcentajeAciertos)}%
                  </Text>
                </div>
              </Center>
            }
          />

          <Stack gap={4}>
            <Text size="xs" c="dimmed"><strong>Tiempo:</strong> {formatearTiempo(resultado.duracionSeg)}</Text>
            <Text size="xs" c="dimmed"><strong>Inicio:</strong> {new Date(resultado.iniciadoEn).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}</Text>
            <Text size="xs" c="dimmed"><strong>Fin:</strong> {new Date(resultado.finalizadoEn).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}</Text>
          </Stack>
        </Group>

        <Divider my="md" />

        {/* Filtros compactos */}
        <Group mb="sm" gap="xs">
          <Button
            variant={filtro === 'todas' ? 'filled' : 'default'}
            color="gray"
            radius="md"
            size="xs"
            onClick={() => setFiltro('todas')}
          >
            Todas ({resultado.totalPreguntas})
          </Button>
          <Button
            variant={filtro === 'correctas' ? 'filled' : 'default'}
            color="gray"
            radius="md"
            size="xs"
            onClick={() => setFiltro('correctas')}
          >
            Correctas ({resultado.correctas})
          </Button>
          <Button
            variant={filtro === 'incorrectas' ? 'filled' : 'default'}
            color="gray"
            radius="md"
            size="xs"
            onClick={() => setFiltro('incorrectas')}
          >
            Incorrectas ({resultado.incorrectas})
          </Button>
          <Button
            variant={filtro === 'enblanco' ? 'filled' : 'default'}
            color="gray"
            radius="md"
            size="xs"
            onClick={() => setFiltro('enblanco')}
          >
            En Blanco ({resultado.enBlanco})
          </Button>
        </Group>

        {/* Preguntas con retroalimentación */}
        <Text size="sm" fw={600} mb="sm">
          Preguntas ({preguntasFiltradas.length})
        </Text>

        <Accordion variant="separated" styles={{ item: { marginBottom: '8px' } }}>
          {preguntasFiltradas.map((pregunta) => (
            <Accordion.Item key={pregunta.orden} value={`pregunta-${pregunta.orden}`}>
              <Accordion.Control>
                <Group justify="space-between">
                  <Text fw={500}>Pregunta {pregunta.orden}</Text>
                  <Group gap="xs">
                    <Badge color="gray" variant="light" size="sm">{pregunta.tipo}</Badge>
                    <Badge
                      color={pregunta.esCorrecta ? 'green' : pregunta.respuestaUsuario === null ? 'gray' : 'red'}
                      variant="light"
                      size="sm"
                      leftSection={
                        pregunta.respuestaUsuario === null ? null : pregunta.esCorrecta ? <IconCheck size={12} /> : <IconX size={12} />
                      }
                    >
                      {pregunta.respuestaUsuario === null ? 'Sin responder' : pregunta.esCorrecta ? 'Correcta' : 'Incorrecta'}
                    </Badge>
                  </Group>
                </Group>
              </Accordion.Control>

              <Accordion.Panel>
                {(() => {
                  // Debug temporal - eliminar después
                  console.log('Pregunta', pregunta.orden, '- respuestaCorrecta:', pregunta.respuestaCorrecta, '- respuestaUsuario:', pregunta.respuestaUsuario);
                  return null;
                })()}
                
                {/* Caso clínico (si existe) */}
                {pregunta.casoEstudioContenido && (
                  <Paper
                    p="sm"
                    mb="sm"
                    withBorder
                    radius="md"
                    style={{
                      backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.6)' : '#fafafa',
                      borderLeft: `3px solid ${colorScheme === 'dark' ? 'rgba(148,163,184,0.4)' : '#e5e7eb'}`,
                    }}
                  >
                    <Text fw={500} size="xs" mb="xs" c="dimmed">
                      CASO CLÍNICO
                    </Text>
                    <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                      {pregunta.casoEstudioContenido}
                    </Text>
                  </Paper>
                )}

                {/* Pregunta */}
                <Text fw={500} size="sm" mb="sm">{pregunta.pregunta}</Text>

                {/* Opciones */}
                <Stack gap="xs" mb="sm">
                  {['a', 'b', 'c', 'd'].map((opcion) => {
                    const textoRespuesta =
                      opcion === 'a' ? pregunta.respuestaA :
                      opcion === 'b' ? pregunta.respuestaB :
                      opcion === 'c' ? pregunta.respuestaC :
                      pregunta.respuestaD;

                    const esRespuestaUsuario = pregunta.respuestaUsuario === opcion;
                    // Normalizar a minúsculas para comparación
                    const esRespuestaCorrecta = pregunta.respuestaCorrecta?.toLowerCase() === opcion;

                    let bgColor = colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.6)' : '#ffffff';
                    let borderColor = colorScheme === 'dark' ? '#475569' : '#e5e7eb';
                    if (esRespuestaCorrecta) {
                      bgColor = colorScheme === 'dark' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.08)';
                      borderColor = colorScheme === 'dark' ? '#22c55e' : '#16a34a';
                    } else if (esRespuestaUsuario && !esRespuestaCorrecta) {
                      bgColor = colorScheme === 'dark' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.08)';
                      borderColor = colorScheme === 'dark' ? '#ef4444' : '#dc2626';
                    }

                    return (
                      <Paper
                        key={opcion}
                        p="sm"
                        withBorder
                        radius="md"
                        style={{
                          backgroundColor: bgColor,
                          borderWidth: esRespuestaCorrecta || esRespuestaUsuario ? 2 : 1,
                          borderColor: borderColor,
                        }}
                      >
                        <Group justify="space-between" align="flex-start" wrap="nowrap">
                          <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
                            <Badge
                              size="sm"
                              variant="light"
                              color="gray"
                              style={{
                                width: 28,
                                height: 28,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 600,
                                fontSize: '12px',
                              }}
                            >
                              {opcion.toUpperCase()}
                            </Badge>
                            <Text size="sm" style={{ flex: 1 }}>{textoRespuesta}</Text>
                          </Group>
                          {esRespuestaCorrecta && (
                            <Badge color="green" variant="filled" size="sm" style={{ fontWeight: 600 }}>CORRECTA</Badge>
                          )}
                          {esRespuestaUsuario && !esRespuestaCorrecta && (
                            <Badge color="red" variant="filled" size="sm" style={{ fontWeight: 600 }}>TU</Badge>
                          )}
                        </Group>
                      </Paper>
                    );
                  })}
                </Stack>

                {/* Retroalimentación */}
                {pregunta.retroalimentacion && (
                  <Paper
                    p="sm"
                    withBorder
                    radius="md"
                    style={{
                      backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.6)' : '#fafafa',
                    }}
                  >
                    <Text fw={500} mb="xs" size="xs">Retroalimentación:</Text>
                    <Text size="xs">{pregunta.retroalimentacion}</Text>
                  </Paper>
                )}
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>

      </Paper>
    </Box>
  );
};

export default ResultadosPage;
